import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEventHandler,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { IconClipboard } from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { acceptAllowsImagePaste, assignFilesToInput } from '../utils/clipboardImage.js';
import { useClipboardImagePaste } from '../utils/useClipboardImagePaste.js';
import { Button } from './Button.js';
import { IconButton } from './IconButton.js';
import { Tooltip } from './Tooltip.js';
import styles from './FilePickerField.module.css';

/** Accept string for catalog / product images (admin uploads). */
export const WP_IMAGE_FILE_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif,.avif';

/** BL / invoice style uploads (PDF + images). */
export const WP_RECEIPT_DOC_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/gif,.pdf';

/** Max upload size for receipt docs (matches Nest `limits.fileSize` on procurement uploads). */
export const WP_RECEIPT_DOC_MAX_BYTES = 15 * 1024 * 1024;

export function formatFileByteSizeFr(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} o`;
  if (bytes < 1024 * 1024) {
    const ko = bytes / 1024;
    return `${ko < 10 ? ko.toFixed(1) : Math.round(ko)} Ko`;
  }
  const mo = bytes / (1024 * 1024);
  return `${mo < 10 ? mo.toFixed(1) : Math.round(mo)} Mo`;
}

export type FilePickerFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'children' | 'className' | 'size'
> & {
  className?: string;
  /** Bordered control vs compact row (button + caption only). */
  variant?: 'field' | 'plain';
  chooseLabel?: string;
  /** Icon-only trigger; `chooseLabel` / `aria-label` still required for a11y. */
  chooseIcon?: ReactNode;
  emptyHint?: string;
  /** Selected file name(s); when set, replaces empty hint in caption area. */
  summary?: ReactNode;
  /** When set (single-file image pickers), shows a small thumbnail via object URL (revoked on change/unmount). */
  imagePreview?: Blob | File | null;
  /** `Button` size for the trigger. */
  triggerSize?: 'xs' | 'sm' | 'md';
};

export const FilePickerField = forwardRef<HTMLInputElement, FilePickerFieldProps>(function FilePickerField(
  {
    variant = 'field',
    chooseLabel = 'Choisir un fichier',
    chooseIcon,
    emptyHint = 'Aucun fichier choisi',
    summary,
    imagePreview = null,
    triggerSize = 'sm',
    disabled,
    id,
    onChange,
    accept,
    multiple,
    name,
    'aria-label': ariaLabel,
    className,
    ...rest
  },
  forwardedRef,
) {
  const autoId = useId();
  const inputId = id ?? `wp-file-${autoId}`;
  const inputNodeRef = useRef<HTMLInputElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const pasteEnabled = acceptAllowsImagePaste(accept);

  const applyPastedFile = useCallback(
    (file: File) => {
      if (disabled) return;
      const input = inputNodeRef.current;
      if (!input) return;
      assignFilesToInput(input, [file]);
    },
    [disabled],
  );

  const { wrapProps, pasteFromClipboard } = useClipboardImagePaste({
    disabled: Boolean(disabled) || !pasteEnabled,
    accept,
    onFile: applyPastedFile,
  });

  useEffect(() => {
    if (!imagePreview) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(imagePreview);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imagePreview]);

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputNodeRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [forwardedRef],
  );

  const open = useCallback(() => {
    if (disabled) return;
    inputNodeRef.current?.click();
  }, [disabled]);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange?.(e);
  };

  const hasSummary = summary !== undefined && summary !== null && summary !== '';
  const summaryText = typeof summary === 'string' && summary.trim() ? summary.trim() : null;

  const captionLabel = (
    <span
      className={cx(
        variant === 'field' ? styles.caption : styles.plainCaption,
        hasSummary && (variant === 'field' ? styles.captionHasValue : styles.plainCaptionValue),
      )}
      id={`${inputId}-caption`}
    >
      {hasSummary ? summary : emptyHint}
    </span>
  );

  const caption =
    summaryText != null ? (
      <Tooltip label={summaryText} fill position="top" portal>
        {captionLabel}
      </Tooltip>
    ) : (
      captionLabel
    );

  const input = (
    <input
      {...rest}
      ref={setInputRef}
      id={inputId}
      type="file"
      className={styles.visuallyHidden}
      accept={accept}
      multiple={multiple}
      name={name}
      disabled={disabled}
      aria-label={`${ariaLabel ?? chooseLabel}${pasteEnabled ? '. Coller une image avec Ctrl+V.' : ''}`}
      aria-describedby={`${inputId}-caption`}
      onChange={handleChange}
    />
  );

  const triggerLabel = ariaLabel ?? chooseLabel;
  const pasteLabel = 'Coller une image';

  const pasteControl = pasteEnabled ? (
    <Tooltip label={`${pasteLabel} (Ctrl+V)`} position="bottom" portal>
      <IconButton
        type="button"
        variant="soft"
        size={triggerSize}
        disabled={disabled}
        onClick={() => void pasteFromClipboard()}
        aria-label={`${pasteLabel} (Ctrl+V)`}
        className={styles.triggerIconBtn}
      >
        <IconClipboard size={16} stroke={1.5} />
      </IconButton>
    </Tooltip>
  ) : null;

  const triggerControl = chooseIcon ? (
    <IconButton
      type="button"
      variant="soft"
      size={triggerSize}
      disabled={disabled}
      onClick={open}
      aria-label={triggerLabel}
      className={styles.triggerIconBtn}
    >
      {chooseIcon}
    </IconButton>
  ) : (
    <Button type="button" variant="soft" size={triggerSize} disabled={disabled} onClick={open}>
      {chooseLabel}
    </Button>
  );

  const trigger =
    chooseIcon != null ? (
      <Tooltip label={triggerLabel} position="bottom" portal>
        {triggerControl}
      </Tooltip>
    ) : (
      triggerControl
    );

  const preview =
    previewUrl != null ? (
      <div className={styles.previewCell} aria-hidden>
        <img src={previewUrl} alt="" className={styles.previewThumb} />
      </div>
    ) : null;

  if (variant === 'plain') {
    return (
      <div className={cx(styles.wrap, className)} {...wrapProps}>
        {input}
        <div className={styles.plainRow}>
          {preview}
          {trigger}
          {pasteControl}
          {caption}
        </div>
      </div>
    );
  }

  return (
    <div className={cx(styles.wrap, className)} {...wrapProps}>
      {input}
      <div className={cx(styles.field, disabled && styles.disabled)}>
        <div style={{ display: 'flex', alignItems: 'stretch', width: '100%', minWidth: 0 }}>
          {preview}
          <div className={cx(styles.triggerCell, Boolean(chooseIcon) && styles.triggerCellIcon)}>
            {trigger}
            {pasteControl}
          </div>
          <div className={styles.captionCell}>{caption}</div>
        </div>
      </div>
    </div>
  );
});
